'use client'

import Image from 'next/image'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  BookOpen,
  BarChart3,
  MessageCircle,
  FileSpreadsheet,
  Download,
  Users,
  TrendingUp,
  Brain,
  ChevronRight,
  Star,
  Shield,
  Zap,
  Upload,
  LifeBuoy,
  Mail,
  AlertTriangle,
  Lock,
  Eye,
  Clock,
  Trash2,
  CheckCircle2,
  Info,
} from 'lucide-react'
import bannerImg from '@/assets/banner-img.svg'
import { SectionHeader } from '@/components/ui/section-header'
import { SiteHeader } from '@/components/layout/site-header'
import {
  PerformanceOverTimeWidget,
  SpeedometerWidget,
  AtRiskStudentsWidget,
} from '@/components/ui/analytics-widgets'
import CountUp from '@/components/ui/count-up'
import PricingPlans from '@/components/sections/pricing'
import SupportForm from '@/components/sections/support-form'
import SupportHub from '@/components/sections/support-hub'
import AboutInteractive from '@/components/sections/about-interactive'
import SiteFooter from '@/components/layout/site-footer'
import TestimonialsCarousel from '@/components/sections/testimonials'
import { PlatformStats } from '@/lib/types/platform-stats.types'
import { Tutorial } from '@/lib/types/tutorial.types'

type HomePageProps = {
  platformStats: PlatformStats | null
  overviewTutorial: Tutorial | null
}

export default function HomePage({ platformStats, overviewTutorial }: HomePageProps) {

  const features = [
    {
      icon: <BookOpen className='h-6 w-6' />,
      title: 'Comprehensive Data Management',
      description:
        'Organize classes, subjects, quizzes, and projects with intuitive interfaces designed for educators.',
      badge: 'Core Feature',
    },
    {
      icon: <Brain className='h-6 w-6' />,
      title: 'AI-Powered Analytics',
      description:
        'Generate intelligent reports and insights from student performance data using advanced AI analysis.',
      badge: 'AI Powered',
    },
    {
      icon: <BarChart3 className='h-6 w-6' />,
      title: 'Interactive Visualizations',
      description:
        'Create stunning charts, heat maps, and graphs to visualize student performance trends.',
      badge: 'Visual Analytics',
    },
    {
      icon: <MessageCircle className='h-6 w-6' />,
      title: 'Intelligent Chatbot',
      description:
        'Ask questions about your data using natural language and get instant insights.',
      badge: 'Smart Assistant',
    },
    {
      icon: <FileSpreadsheet className='h-6 w-6' />,
      title: 'Excel Analysis',
      description:
        'Upload Excel files and get immediate analysis without storing sensitive data.',
      badge: 'Privacy First',
    },
    {
      icon: <Download className='h-6 w-6' />,
      title: 'Professional Reports',
      description:
        'Export beautiful PDF reports and visualizations for presentations and documentation.',
      badge: 'Export Ready',
    },
  ]

  // Inline sample data for Customize section widgets
  const sampleClassTrend = [
    { week: 'W1', score: 65 },
    { week: 'W2', score: 68 },
    { week: 'W3', score: 72 },
    { week: 'W4', score: 70 },
    { week: 'W5', score: 75 },
    { week: 'W6', score: 79 },
  ]

  type RiskLevel = 'high' | 'medium' | 'low'
  const sampleAtRisk: {
    name: string
    subject: string
    riskLevel: RiskLevel
  }[] = [
      { name: 'A. Patel', subject: 'Mathematics', riskLevel: 'high' },
      { name: 'J. Kim', subject: 'Science', riskLevel: 'medium' },
      { name: 'R. Garcia', subject: 'English', riskLevel: 'high' },
      { name: 'M. Singh', subject: 'History', riskLevel: 'low' },
      { name: 'L. Chen', subject: 'Geography', riskLevel: 'medium' },
    ]

  const stats = [
    {
      value: platformStats?.educators_served ?? 0,
      suffix: '+',
      label: 'Educators Served',
      compact: true,
      icon: <Users className='h-5 w-5' />,
    },
    {
      value: platformStats?.student_records ?? 0,
      suffix: '+',
      label: 'Student Records',
      compact: true,
      icon: <TrendingUp className='h-5 w-5' />,
    },
    {
      value: platformStats?.reports_generated ?? 0,
      suffix: '+',
      label: 'Reports Generated',
      compact: true,
      icon: <BarChart3 className='h-5 w-5' />,
    },
    {
      value: platformStats?.uptime_percentage ?? 99.9,
      suffix: '%',
      label: 'Uptime Guaranteed',
      compact: false,
      decimals: 1,
      icon: <Shield className='h-5 w-5' />,
    },
  ]

  return (
    <div className='min-h-screen bg-gradient-soft'>
      {/* Header */}
      <SiteHeader />

      {/* Hero Section */}
      <section
        id='hero'
        className='relative pt-2 pb-12 md:py-16 xl:py-20 2xl:py-28 overflow-hidden'
      >
        <div className='container mx-auto px-4'>
          <div className='grid gap-6 lg:grid-cols-2 lg:gap-12'>
            <div className='space-y-8 order-2 lg:order-1'>
              <SectionHeader
                eyebrow='AI-Powered Platform'
                title={
                  <>
                    Turn student data into clear,{' '}
                    <span className='text-[hsl(var(--primary-blue))]'>
                      actionable insights
                    </span>{' '}
                    — instantly
                  </>
                }
                subtitle='Empower educators with effortless, AI-driven analytics that transform raw student data into beautiful graphs, charts, and professional reports in just a few clicks.'
                align='left'
                size='xl'
              />
              <div className='flex flex-col sm:flex-row gap-4'>
                <Button
                  variant='hero'
                  size='lg'
                  asChild
                  className='group box-border h-10 sm:h-11 text-sm px-4 sm:text-base sm:px-6'
                >
                  <Link href='/signup'>
                    Start Your Free Trial
                    <ChevronRight className='ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform' />
                  </Link>
                </Button>
              </div>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-border'>
                {stats.map((stat, index) => (
                  <div key={index} className='text-center space-y-2'>
                    <div className='flex items-center justify-center text-emerald'>
                      {stat.icon}
                    </div>
                    <div className='font-bold text-2xl text-foreground'>
                      <CountUp
                        end={stat.value}
                        suffix={stat.suffix ?? ''}
                        decimals={stat.decimals ?? 0}
                        compact={stat.compact ?? true}
                      />
                    </div>
                    <div className='text-sm text-muted-foreground'>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className='flex relative h-72 lg:h-auto order-1 lg:order-2'>
              <Image
                src={bannerImg}
                alt='Educational platform'
                fill
                className='w-full h-auto object-cover'
                priority
              />
              <div className=''>
                {/* Image placeholder since original asset not present */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECURITY & PRIVACY */}
      <section id='security-privacy' className='py-12 md:py-16 bg-gradient-soft border-y border-border/50'>
        <div className='container mx-auto px-4'>
          <div className='max-w-4xl mx-auto'>
            <div className='text-center mb-10'>
              <div className='flex items-center justify-center gap-2 mb-4'>
                <Shield className='h-8 w-8 text-primary' />
                <h2 className='text-2xl md:text-3xl font-bold text-foreground'>
                  Security & Data Privacy
                </h2>
              </div>
              <p className='text-muted-foreground'>
                Our platform is designed with student privacy as the top priority.
              </p>
            </div>

            <div className='grid md:grid-cols-2 gap-8 bg-card/50 backdrop-blur-sm border border-border/60 rounded-xl p-8 shadow-elegant'>
              <div className='space-y-4'>
                <h3 className='text-lg font-semibold flex items-center gap-2 text-primary'>
                  <Eye className='h-5 w-5' />
                  Data Guidelines
                </h3>
                <ul className='space-y-3'>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>Use Anonymous Codes:</strong> Use identifiers like &quot;Student A&quot; or &quot;S001&quot; instead of real names.</span>
                  </li>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>Generic Institute Names:</strong> Use generic names like &quot;Institute A&quot; or &quot;Demo School&quot; instead of real school/college names.</span>
                  </li>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>Generic Class Names:</strong> Use generic labels like &quot;Grade 10 - Section A&quot; or &quot;Math 101&quot; instead of specific class identifiers.</span>
                  </li>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>FERPA Ready:</strong> De-identified data ensures compliance with student privacy laws.</span>
                  </li>
                </ul>
              </div>

              <div className='space-y-4'>
                <h3 className='text-lg font-semibold flex items-center gap-2 text-primary'>
                  <Lock className='h-5 w-5' />
                  Technical Protection
                </h3>
                <ul className='space-y-3'>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>Memory-Only Processing:</strong> Data stays in RAM only; no permanent storage is used for excels.</span>
                  </li>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>Auto-Deletion:</strong> All uploaded excel data is automatically cleared after analysis.</span>
                  </li>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>2-Hour Timeout:</strong> Mandatory session expiry for enhanced session protection for excel files.</span>
                  </li>
                  <li className='flex items-start gap-3 text-sm'>
                    <span className='text-primary mt-1'>•</span>
                    <span><strong>PII Scanning:</strong> Automatic detection and warnings if real names are found.</span>
                  </li>
                </ul>
              </div>

              <div className='md:col-span-2 pt-6 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground'>
                <div className='flex items-center gap-4'>
                  <Link href='/privacy' className='hover:text-primary underline'>Privacy Policy</Link>
                  <Link href='/terms' className='hover:text-primary underline'>Terms of Service</Link>
                </div>
                <div className='flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 text-green-500' />
                  <span>Industry-standard HTTPS encryption active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section id='about' className='py-12 md:py-16 bg-card'>
        <div className='container mx-auto px-4'>
          <SectionHeader
            eyebrow='About Us'
            title='Our mission'
            subtitle='We help educators turn complex data into clear, actionable insights—so they can focus on teaching and student growth.'
            align='center'
            size='lg'
            className='mb-10'
          />

          <AboutInteractive />
        </div>
      </section>

      {/* Features */}
      <section id='features' className='py-12 md:py-20 bg-background'>
        <div className='container mx-auto px-4'>
          <SectionHeader
            eyebrow='Analytics Made Simple'
            title='Insights made powerful. Teaching made impactful.'
            subtitle='By simplifying complex data, we free teachers to focus on what matters most — personalizing instruction, driving student growth, and making confident, evidence-based decisions.'
            align='center'
            size='lg'
            className='mb-16'
          />
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {features.map((feature, index) => (
              <Card
                key={index}
                className='group hover:shadow-elegant transition-all duration-300 border-border/50 hover:border-accent/50'
              >
                <CardHeader className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-soft border group-hover:bg-gradient-primary group-hover:text-white transition-all duration-300'>
                      {feature.icon}
                    </div>
                    <Badge variant='secondary' className='text-xs'>
                      {feature.badge}
                    </Badge>
                  </div>
                  <CardTitle className='text-xl font-semibold text-foreground group-hover:text-accent transition-colors'>
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className='text-muted-foreground leading-relaxed'>
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id='testimonials' className='py-12 md:py-20 bg-background'>
        <div className='container mx-auto px-4'>
          <SectionHeader
            eyebrow='What educators say'
            title='Trusted by teachers and leaders'
            subtitle='Real stories from classrooms and schools using EduBridgeInsight to drive student success.'
            align='center'
            size='lg'
            className='mb-12'
          />
          <TestimonialsCarousel />
        </div>
      </section>

      {/* Pricing */}
      <section id='pricing' className='py-12 md:py-20 bg-background'>
        <div className='container mx-auto px-4'>
          <SectionHeader
            title='Transparent pricing for every need'
            subtitle='Simple plans for individual teachers, schools, and districts — upgrade anytime.'
            align='center'
            size='lg'
            className='mb-12'
          />
          <PricingPlans />
        </div>
      </section>

      {/* Demo: how the product works */}
      <section id='resources' className='py-12 md:py-20 bg-card'>
        <div className='container mx-auto px-4'>
          <SectionHeader
            eyebrow='Quick Demo'
            title='See how EduBridgeInsight works in minutes'
            subtitle='Upload data (or use samples), get AI-powered insights instantly, and export beautiful, shareable reports.'
            align='center'
            size='lg'
            className='mb-12'
          />

          <div className='grid lg:grid-cols-2 gap-10 items-stretch'>
            {/* Video card — Overview tutorial from Guides & Video Tutorials */}
            <div className='relative rounded-xl overflow-hidden border border-border bg-background shadow-subtle'>
              <div className='aspect-video w-full bg-muted'>
                {overviewTutorial?.video_url ? (
                  <video
                    key={overviewTutorial.tutorial_id}
                    src={overviewTutorial.video_url}
                    controls
                    playsInline
                    preload='metadata'
                    poster={overviewTutorial.thumbnail_url ?? undefined}
                    className='h-full w-full object-contain bg-black'
                    title={overviewTutorial.title}
                  />
                ) : (
                  <div className='flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center'>
                    <p className='text-sm font-medium text-foreground'>
                      Overview video unavailable
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      Enable a walkthrough tutorial in admin to show it here.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Step-by-step */}
            <div className='rounded-xl border border-border bg-background p-6 shadow-subtle flex flex-col justify-center'>
              <div className='space-y-6'>
                <div className='flex items-start gap-4'>
                  <div className='h-9 w-9 rounded-lg bg-gradient-soft border flex items-center justify-center flex-shrink-0'>
                    <Upload className='h-5 w-5 text-[hsl(var(--primary-blue))]' />
                  </div>
                  <div>
                    <h3 className='text-lg font-semibold text-foreground'>
                      Upload or use samples
                    </h3>
                    <p className='text-muted-foreground'>
                      Bring your CSV/Excel file or try with ready-made class
                      data to explore features quickly.
                    </p>
                  </div>
                </div>
                <div className='flex items-start gap-4'>
                  <div className='h-9 w-9 rounded-lg bg-gradient-soft border flex items-center justify-center flex-shrink-0'>
                    <Brain className='h-5 w-5 text-[hsl(var(--primary-blue))]' />
                  </div>
                  <div>
                    <h3 className='text-lg font-semibold text-foreground'>
                      AI analyzes performance
                    </h3>
                    <p className='text-muted-foreground'>
                      Automatic insights on growth, trends, and attention
                      areas—no manual crunching.
                    </p>
                  </div>
                </div>
                <div className='flex items-start gap-4'>
                  <div className='h-9 w-9 rounded-lg bg-gradient-soft border flex items-center justify-center flex-shrink-0'>
                    <FileSpreadsheet className='h-5 w-5 text-[hsl(var(--primary-blue))]' />
                  </div>
                  <div>
                    <h3 className='text-lg font-semibold text-foreground'>
                      Share visuals and reports
                    </h3>
                    <p className='text-muted-foreground'>
                      Export polished charts and PDF reports to share with teams
                      and parents.
                    </p>
                  </div>
                </div>
              </div>

              <div className='mt-8 flex flex-wrap gap-3'>
                <Button variant='hero' asChild>
                  <Link href='/signup'>Start Your Free Trial</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Support */}
      <section id='support' className='py-12 md:py-20 bg-background'>
        <div className='container mx-auto px-4'>
          <SectionHeader
            title='Need help?'
            subtitle='Reach our help desk through the contact form, or report an issue for quick technical support.'
            align='center'
            size='lg'
            className='mb-10'
          />
          <div className='max-w-full md:max-w-[800px] mx-auto w-full'>
            {/* Support hub (tabs + quick list) */}
            {/* <SupportHub /> */}

            {/* In-page support form */}
            <div id='support-form'>
              <SupportForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  )
}
